import { useMutation } from '@apollo/client';
import { Button, Meta, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import { REMOVE_RFID_TOKEN } from 'graphql/mutations/removeRfidToken';
import { GET_CUSTOMER_RFID_TOKENS } from 'graphql/queries/getCustomerRfidTokens';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRfidStore } from 'stores/rfidStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Mutation,
  MutationDeleteRfidTokenArgs,
} from 'types/generatedSchemaTypes';
import { useUiStore } from 'stores/uiStore';

const ConfirmRfidTagRemoval: React.FC = () => {
  const { t } = useTranslation();
  const serialNumber = useRfidStore((state) => state.currentSerialNumber);
  const tagName = useRfidStore((state) => state.currentTagName);

  const [removeRfidToken, { loading }] = useMutation<
    Mutation,
    MutationDeleteRfidTokenArgs
  >(REMOVE_RFID_TOKEN, {
    onCompleted: () => {
      const text = t('success.RfidTokenRemoval', {
        tagName: tagName,
      });
      showToast(`${text}`, 'success');
      useUiStore.getState().setSheetModal('');
    },
    onError: () =>
      showToast(t('error.RfidTokenRemoval', { tagName: tagName }), 'error'),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_CUSTOMER_RFID_TOKENS }],
  });

  const handleDeleteTag = () => {
    if (!serialNumber) {
      return;
    }
    removeRfidToken({
      variables: {
        input: {
          serialNumber: serialNumber,
        },
      },
    });
  };

  const handleCancel = () => useUiStore.getState().setSheetModal('');

  return (
    <StyledWrapper mb={25} mt={25}>
      <StyledWrapper mb={25} center>
        <Meta center color={theme.colors.gray300}>
          {t('account.tagsAndCards.removeTagConfirmation', {
            tagName: tagName,
          })}
        </Meta>
      </StyledWrapper>
      <StyledWrapper mb={25}>
        <Button
          loading={loading}
          testID="remove-button"
          size="l"
          text={loading ? t('button.removing') : t('button.remove')}
          onPress={handleDeleteTag}
          disabled={loading}
        />
      </StyledWrapper>
      <StyledWrapper mb={25}>
        <Button
          testID="cancel-button"
          size="l"
          text={t('button.cancel')}
          onPress={handleCancel}
          buttonType="tertiary"
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default ConfirmRfidTagRemoval;
